<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { useProductStore } from '@/store/product.store';
import { useCategoryStore } from '@/store/category.store';
import { useCatalogueCollections } from '@/composables/catalogue/useCatalogueCollections';
import CatalogueCollectionsPanel from '@/components/catalogue/CatalogueCollectionsPanel.vue';
import CatalogueDeleteConfirm from '@/components/catalogue/CatalogueDeleteConfirm.vue';
import {
  isGlobalSucess,
  GlobalSucessMessage,
  isGlobalError,
  GlobalErrorMessage,
} from '@/refs/common.refs';
import { organizationIdCommerce } from '@/refs/commerce.refs';
import { currentLocale } from '@/refs/product.refs';

const route = useRoute();
const productStore = useProductStore();
const categoryStore = useCategoryStore();

// Filtrage par organisation quand on arrive depuis /clients/{id} → « Collections ».
const filterOrgId = computed(
  () => (route.query.organizationId as string | undefined) || undefined,
);
if (filterOrgId.value) organizationIdCommerce.value = filterOrgId.value;

const showSuccessToast = (msg: string) => {
  (GlobalSucessMessage as any).value = msg;
  isGlobalSucess.value = true;
  setTimeout(() => {
    isGlobalSucess.value = false;
  }, 3000);
};
const showErrorToast = (msg: string) => {
  (GlobalErrorMessage as any).value = msg;
  isGlobalError.value = true;
  setTimeout(() => {
    isGlobalError.value = false;
  }, 3000);
};

const { data: dataGetProduct, refetch: refetchProduct } = useQuery({
  queryKey: computed(() => [
    'GetProduct',
    currentLocale.value,
    filterOrgId.value,
  ]),
  queryFn: () => productStore.getProduct(filterOrgId.value),
});
const {
  data: dataGetProductForCollections,
  refetch: refetchProductForCollections,
} = useQuery({
  queryKey: computed(() => [
    'GetProductForCollections',
    currentLocale.value,
    filterOrgId.value,
  ]),
  queryFn: () => productStore.getProductForCollections(filterOrgId.value),
});
const { data: dataGetCategories, refetch: refetchCategories } = useQuery({
  queryKey: computed(() => ['GetCategories', filterOrgId.value]),
  queryFn: () => categoryStore.getCategory(),
});

const col = useCatalogueCollections(
  dataGetCategories,
  dataGetProductForCollections,
  refetchProductForCollections,
  refetchCategories,
  showSuccessToast,
  showErrorToast,
  refetchProduct,
);
</script>

<template>
  <div class="col-page">
    <div class="col-page__hd">
      <div>
        <div class="col-page__bc">
          <RouterLink
            v-if="filterOrgId"
            :to="`/clients/${filterOrgId}`"
            class="col-page__bc-link"
          >
            <i class="pi pi-arrow-left" style="font-size: 0.6875rem" />
            Client
          </RouterLink>
          <template v-else>
            <span>Catalogue</span>
          </template>
          <i class="pi pi-chevron-right" style="font-size: 0.625rem" />
          <span style="color: var(--nxt-primary)">Collections</span>
        </div>
        <h1 class="col-page__title">Gestion des Collections</h1>
      </div>
      <button class="col-page__btn" @click="col.onNewColInput('fr', '')">
        <i class="pi pi-plus" /> Nouvelle collection
      </button>
    </div>

    <div class="col-page__wrap">
      <CatalogueCollectionsPanel
        :selected-module="null"
        :full-width="true"
        :show-collections="true"
        :visible-collections="col.visibleCollections.value"
        :dragging-product="col.draggingProduct.value"
        :drag-over-collection="col.dragOverCollection.value"
        :is-filtering="col.isFiltering.value"
        :editing-section="col.editingSection.value"
        :edit-cat-lang="col.editCatLang.value"
        :edit-cat-desig-fr="col.editCatDesigFr.value"
        :edit-cat-desig-nl="col.editCatDesigNl.value"
        :edit-cat-desig-en="col.editCatDesigEn.value"
        :edit-cat-desc-fr="col.editCatDescFr.value"
        :edit-cat-desc-nl="col.editCatDescNl.value"
        :edit-cat-desc-en="col.editCatDescEn.value"
        :edit-cat-modules="col.editCatModules.value"
        :is-saving-cat-edit="col.isSavingCatEdit.value"
        :is-auto-translating-cat-desig="col.isAutoTranslatingCatDesig.value"
        :is-auto-translating-cat-desc="col.isAutoTranslatingCatDesc.value"
        :moving-category-id="col.movingCategoryId.value"
        :deleting-product-id="col.deletingProductId.value"
        :show-add-input="col.showAddInput.value"
        :new-col-fr="col.newColFr.value"
        :new-col-nl="col.newColNl.value"
        :new-col-en="col.newColEn.value"
        :new-col-lang="col.newColLang.value"
        :new-col-modules="col.newColModules.value"
        :is-auto-translating-new-col="col.isAutoTranslatingNewCol.value"
        :is-adding-collection="col.isAddingCollection.value"
        :expanded-sections="col.expandedSections.value"
        @close="() => {}"
        @create-collection="col.showAddInput.value = true"
        @toggle-section="(key: string) => col.toggleSection(key)"
        @start-edit="
          (id: string | null, name: string) => col.startEdit(id, name)
        "
        @cancel-edit="col.cancelEdit"
        @save-edit="(id: string | null, name: string) => col.saveEdit(id, name)"
        @delete-collection="
          (id: string | null, name: string) => col.deleteCollection(id, name)
        "
        @move-up="(i: number) => col.moveCategoryUp(i)"
        @move-down="(i: number) => col.moveCategoryDown(i)"
        @remove-product="
          (pid: string, cid: string | null) =>
            col.removeProductFromCategory(pid, cid)
        "
        @dragover="(key: string) => col.onDragOver(key)"
        @dragleave="col.onDragLeave"
        @drop="(id: string | null, name: string) => col.onDrop(id, name)"
        @update:edit-cat-lang="
          (v: any) => {
            col.editCatLang.value = v;
          }
        "
        @update:edit-cat-desig-fr="
          (v: string) => {
            col.editCatDesigFr.value = v;
          }
        "
        @update:edit-cat-desig-nl="
          (v: string) => {
            col.editCatDesigNl.value = v;
          }
        "
        @update:edit-cat-desig-en="
          (v: string) => {
            col.editCatDesigEn.value = v;
          }
        "
        @update:edit-cat-desc-fr="
          (v: string) => {
            col.editCatDescFr.value = v;
          }
        "
        @update:edit-cat-desc-nl="
          (v: string) => {
            col.editCatDescNl.value = v;
          }
        "
        @update:edit-cat-desc-en="
          (v: string) => {
            col.editCatDescEn.value = v;
          }
        "
        @update:edit-cat-modules="
          (v: string[]) => {
            col.editCatModules.value = v as typeof col.editCatModules.value;
          }
        "
        @cat-desig-input="
          (lang: 'fr' | 'nl' | 'en', val: string) =>
            col.onCatDesigInput(lang, val)
        "
        @cat-desc-input="
          (lang: 'fr' | 'nl' | 'en', val: string) =>
            col.onCatDescInput(lang, val)
        "
        @show-add-input="col.showAddInput.value = true"
        @cancel-add="col.cancelAdd"
        @confirm-add="col.confirmAdd"
        @update:new-col-fr="
          (v: string) => {
            col.newColFr.value = v;
          }
        "
        @update:new-col-nl="
          (v: string) => {
            col.newColNl.value = v;
          }
        "
        @update:new-col-en="
          (v: string) => {
            col.newColEn.value = v;
          }
        "
        @update:new-col-lang="
          (v: any) => {
            col.newColLang.value = v;
          }
        "
        @update:new-col-modules="
          (v: string[]) => {
            col.newColModules.value = v as typeof col.newColModules.value;
          }
        "
        @new-col-input="
          (lang: 'fr' | 'nl' | 'en', val: string) =>
            col.onNewColInput(lang, val)
        "
        @visit="() => {}"
      />
    </div>

    <CatalogueDeleteConfirm
      :model-value="col.confirmDeleteName.value"
      @confirm="col.confirmDeleteCollection"
      @cancel="col.cancelDeleteCollection"
    />
  </div>
</template>

<style scoped>
.col-page {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.75rem 2rem 3rem;
  width: 100%;
}

.col-page__hd {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.col-page__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #aaa;
  margin-bottom: 0.25rem;
}
.col-page__bc-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #aaa;
  text-decoration: none;
}
.col-page__bc-link:hover {
  color: var(--nxt-primary, #f68402);
}
.col-page__title {
  margin: 0;
  font-size: 1.625rem;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
}
[data-theme='dark'] .col-page__title {
  color: #f0f0f0;
}

.col-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.625rem 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
  box-shadow: 0 4px 14px -3px rgba(0, 0, 0, 0.4);
  transition: all 0.15s;
}
.col-page__btn:hover {
  box-shadow: 0 6px 20px -3px rgba(0, 0, 0, 0.5);
  transform: translateY(-1px);
}

.col-page__wrap {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
[data-theme='dark'] .col-page__wrap {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
</style>
